import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

/**
 * Builder live-feedback hooks — call the existing `/builder` backends so the
 * scenario builder can show validation + cost as the operator edits.
 *
 * Both hooks key off a stable hash of the document so identical documents
 * dedupe through TanStack Query's cache, and they only fire when `enabled`
 * (the builder gates this on a parseable YAML document). They mirror the
 * existing read-only hook conventions (see useCost.ts): no hand-written
 * per-endpoint client methods — everything flows through `apiFetch(cmd(...))`.
 */

export interface ValidateScenarioYamlResponse {
  valid: boolean;
  errors: Array<{ path: string; message: string }>;
  warnings: Array<{ path: string; message: string }>;
}

export interface CostPreviewResponse {
  hourly_usd: number;
  eight_hour_usd: number;
  breakdown: Array<{
    name: string;
    instance_type: string;
    count: number;
    hourly_usd: number;
    line_hourly_usd: number;
  }>;
  estimated_for: string[];
}

export const builderKeys = {
  all: ['builder'] as const,
  validate: (docHash: string) => [...builderKeys.all, 'validate', docHash] as const,
  costPreview: (docHash: string) => [...builderKeys.all, 'cost-preview', docHash] as const,
};

export function useBuilderValidate(options: {
  document: Record<string, unknown>;
  enabled: boolean;
}) {
  const docHash = JSON.stringify(options.document);
  return useQuery<ValidateScenarioYamlResponse>({
    queryKey: builderKeys.validate(docHash),
    enabled: options.enabled,
    staleTime: 10_000,
    queryFn: async ({ signal }) =>
      apiFetch<ValidateScenarioYamlResponse>('/builder/validate', {
        method: 'POST',
        body: { document: options.document },
        signal,
      }),
  });
}

export function useBuilderCostPreview(options: {
  document: Record<string, unknown>;
  enabled: boolean;
}) {
  const docHash = JSON.stringify(options.document);
  return useQuery<CostPreviewResponse>({
    queryKey: builderKeys.costPreview(docHash),
    enabled: options.enabled,
    staleTime: 10_000,
    queryFn: async ({ signal }) =>
      apiFetch<CostPreviewResponse>('/builder/cost-preview', {
        method: 'POST',
        body: { document: options.document },
        signal,
      }),
  });
}
